import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";

import { buildBasemapStyleRuntime } from "@/features/map/basemap-styles";
import { api } from "@/lib/api";

const DEFAULT_CENTER = { lng: -75.5812, lat: 6.2442 };

export default function LocationPickerMap({
  value,
  onChange,
  searchContext = null,
  className = "",
  helpText = "Toque el mapa para ubicar el punto aproximado.",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);
  const lastGeocodedQueryRef = useRef("");
  const [confirmed, setConfirmed] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState("idle");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const center = initialValueRef.current || DEFAULT_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildBasemapStyleRuntime("vibrante"),
      center: [center.lng, center.lat],
      zoom: 13.3,
      pitch: 18,
      bearing: -12,
      attributionControl: false,
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    const marker = new maplibregl.Marker({
      color: "#E14A39",
      draggable: true,
    })
      .setLngLat([center.lng, center.lat])
      .addTo(map);
    marker.on("dragend", () => {
      const coordinate = marker.getLngLat();
      setConfirmed(false);
      onChangeRef.current?.({ lng: coordinate.lng, lat: coordinate.lat });
    });
    markerRef.current = marker;

    map.on("click", (event) => {
      const nextValue = {
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
      };
      marker.setLngLat([nextValue.lng, nextValue.lat]);
      setConfirmed(false);
      onChangeRef.current?.(nextValue);
    });

    return () => {
      marker.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const marker = markerRef.current;
    const nextValue = value || DEFAULT_CENTER;
    marker?.setLngLat([nextValue.lng, nextValue.lat]);

    if (value) {
      map.easeTo({
        center: [value.lng, value.lat],
        duration: 500,
      });
    }
  }, [value]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !searchContext) {
      return undefined;
    }

    const municipio = searchContext.municipio || "Medellin";
    const isUsefulPart = (part) => Boolean(part) && String(part).trim().toLowerCase() !== "no aplica";
    const candidates = [
      [searchContext.direccion, searchContext.barrio, searchContext.comuna, municipio, "Antioquia", "Colombia"],
      [searchContext.barrio, searchContext.comuna, municipio, "Antioquia", "Colombia"],
      [searchContext.comuna, municipio, "Antioquia", "Colombia"],
    ]
      .map((parts) => parts.filter(isUsefulPart).join(", "))
      .filter((query) => query.replace(/,\s*/g, "").trim().length >= 5);

    const query = candidates[0] || "";
    if (!query || query === lastGeocodedQueryRef.current) {
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setGeocodeStatus("searching");
      for (const candidate of candidates) {
        try {
          const response = await api.get("/navigation/geocode", {
            params: { q: candidate, limit: 1 },
          });
          const result = response.data?.results?.[0];
          if (!result || typeof result.lat !== "number" || typeof result.lng !== "number") {
            continue;
          }
          if (!active) {
            return;
          }

          const nextValue = { lat: result.lat, lng: result.lng };
          lastGeocodedQueryRef.current = query;
          markerRef.current?.setLngLat([nextValue.lng, nextValue.lat]);
          setConfirmed(false);
          setGeocodeStatus("found");
          map.flyTo({
            center: [nextValue.lng, nextValue.lat],
            zoom: searchContext.direccion ? 16 : 14.8,
            duration: 800,
          });
          onChangeRef.current?.(nextValue);
          return;
        } catch (_error) {
          // El usuario puede seguir marcando manualmente si no hay geocodificacion disponible.
        }
      }
      lastGeocodedQueryRef.current = query;
      if (active) {
        setGeocodeStatus("unavailable");
      }
    }, 700);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [searchContext]);

  return (
    <div className={`space-y-3 ${className}`}>
      <div ref={containerRef} className="h-64 w-full overflow-hidden rounded-[24px] border border-primary/10" />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-5 text-mutedForeground">
          {geocodeStatus === "searching" ? "Buscando la dirección y ubicando el punto automáticamente..." : null}
          {geocodeStatus === "found" ? "Dirección ubicada automáticamente. Ajuste el pin rojo si es necesario." : null}
          {geocodeStatus === "unavailable" ? `${helpText} No encontramos la dirección automáticamente; puede mover el pin rojo.` : null}
          {geocodeStatus === "idle" ? `${helpText} También puede arrastrar el pin rojo.` : null}
        </p>
        <button
          type="button"
          onClick={() => setConfirmed(true)}
          className={`shrink-0 rounded-2xl border px-4 py-2 text-xs font-semibold transition ${
            confirmed
              ? "border-primary/20 bg-primary/10 text-primary"
              : "border-primary/20 bg-white text-primary hover:bg-primary/5"
          }`}
        >
          {confirmed ? "Ubicación confirmada" : "Confirmar este punto"}
        </button>
      </div>
    </div>
  );
}
